import { Card, Container, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import {
  AutocompleteInput,
  Identifier,
  ReferenceInput,
  SimpleForm,
  required,
  useDataProvider,
  useNotify,
  usePermissions,
  useRedirect,
} from "react-admin";
import { SubmitHandler } from "react-hook-form";
import { Navigate } from "react-router";
import { CrmDataProvider } from "../providers/types";

type BuyerCreateData = {
  id_pessoa: Identifier;
};

const optionPersonsRenderer = (
  choice: {
    id?: number;
    nome_razao?: string;
    nome_fantasia?: string;
    cnpj?: string | number;
    cpf?: string | number;
  } | null,
) => {
  if (!choice) return "";
  return [
    choice.id,
    choice.nome_razao,
    choice.nome_fantasia ? `- ${choice.nome_fantasia}` : null,
    choice.cnpj ? `- ${choice.cnpj}` : null,
    choice.cpf ? `- ${choice.cpf}` : null,
  ]
    .filter(Boolean)
    .join(" ");
};

export function BuyersCreate() {
  const { isPending, permissions } = usePermissions();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const { mutate } = useMutation({
    mutationKey: ["buyers-create"],
    mutationFn: async (data: BuyerCreateData) => {
      return dataProvider.create("buyers", { data });
    },
    onSuccess: () => {
      notify("Comprador criado com sucesso.");
      redirect("/buyers");
    },
    onError: () => {
      notify("Não foi possível criar o comprador.", {
        type: "error",
      });
    },
  });
  const onSubmit: SubmitHandler<BuyerCreateData> = async (data) => {
    mutate(data);
  };

  if (isPending) {
    return null;
  }

  if (permissions !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <SimpleForm onSubmit={onSubmit as SubmitHandler<any>}>
          <Typography variant="h6">Novo comprador</Typography>
          <ReferenceInput
            source="id"
            reference="persons"
            perPage={50}
            filterToQuery={(pesquisa: string) => ({ pesquisa })}
          >
            <AutocompleteInput
              label="Pessoa"
              helperText="Pesquisar por ID, nome, CPF ou CNPJ"
              optionText={optionPersonsRenderer}
              translateChoice={false}
              validate={required()}
              shouldRenderSuggestions={(val: string) => val.trim().length > 2}
            />
          </ReferenceInput>
        </SimpleForm>
      </Card>
    </Container>
  );
}
